import { useEffect, useState, type ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";
import {
  Link,
  useHistory,
  useLocation,
  type LinkProps,
} from "react-router-dom";
import { z } from "zod";
import {
  IconStethoscope,
  IconFolderOpen,
  IconPlus,
  IconNews,
} from "@tabler/icons-react";
import LinkIcon from "../../assets/icons/link-simple.svg?react";
import ChatWithPaperIcon from "../../assets/icons/chat-with-paper.svg?react";
import { displayToast } from "../../components/app/AppToast";
import ExpandableText from "../../components/app/ExpandableText";
import ProfileHeader from "../../components/app/headers/ProfileHeader";
import Loader from "../../components/Loader";
import AddProfilePictureModal from "../../components/profile/AddProfilePictureModal";
import {
  LatestAiChatCard,
  pickLatestAiChat,
} from "../../components/profile/LatestAiChatCard";
import ProfilePicture from "../../components/ProfilePicture";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import PlaylistCard from "../../components/cards/PlaylistCard";
import ShareSheet from "../../components/SharedBySheet";
import ShareActivity from "../../components/gamification/ShareActivity";
import AppCarousel from "../../components/app/AppCarousel";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { Avatar } from "../../components/ui/Avatar";
import { Button } from "../../components/ui/Button";
import { firstVisitFlagsStore } from "../../domains/user/stores/firstVisitFlagsStore";
import {
  useCurrentUser,
  reloadCurrentUser,
  type CurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { getItemTranslation } from "../../domains/app/utils/getItemTranslation";
import { renderDate } from "../../domains/app/utils/renderDate";
import { shareUrl } from "../../domains/app/utils/shareUrl";
import {
  gaEventUserShareProfile,
  gaEventUserViewProfile,
  gaEventUserViewOwnProfile,
} from "../../tools/analytics/domains/user.analytics";
import { gaEventSelectItemPlaylist } from "../../tools/analytics/domains/playlist.analytics";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import { useApiQuery } from "../../services/api";
import { useConversationEngine } from "../../domains/assistant/useConversationEngine";
import { InterestsSheet } from "../../components/interests/InterestsSheet";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { SectionLink } from "../../components/ui/SectionLink";
import { TabButton } from "../../components/ui/TabButton";

enum MainTab {
  INFO = "info",
  LIBRARY = "library",
}

export default function ProfilePage({
  isMe,
  id,
}: {
  isMe: boolean;
  id?: string;
}) {
  // Router
  const history = useHistory();
  const location = useLocation();
  const userId = id ?? null;
  // State
  const [isBioTextExpanded, setBioTextExpanded] = useState(false);
  const [isProfilePictureModalVisible, setProfilePictureModalVisible] =
    useState(false);
  const [isInterestsSheetOpen, setInterestsSheetOpen] = useState(false);
  // Derived
  const searchParams = new URLSearchParams(location.search);
  const activeMainTab = z
    .enum(MainTab)
    .catch(isMe ? MainTab.LIBRARY : MainTab.INFO)
    .parse(searchParams.get("mainTab"));
  // Custom
  const { t } = useTranslation();
  const addJournalsAvatarClass =
    "border-ds-primary bg-ds-primary text-ds-on-primary";
  const interestChipClass =
    "rounded-lg border border-ds-border-selected bg-ds-secondary px-6 py-1.5 text-[13px] font-medium text-ds-primary";
  const { currentUser, isOnboarded } = useCurrentUser();
  const { publicPlaylistLink, gptNewChatPageLink } = usePageLinks(null);
  const targetId = isMe ? (currentUser?.id ?? null) : userId;
  const userQuery = useApiQuery(
    "/webapp/private/users/:id",
    targetId !== null && isOnboarded ? { param: { id: targetId } } : false
  );
  // Self-only "Your interests": the user's selected JuisciTopics, resolved to
  // display names (Topics chantier). Disabled on other users' profiles.
  const myTopicsQuery = useApiQuery(
    "/webapp/private/users/me/topics",
    isMe ? {} : false
  );
  // Fire analytics + refetch on mount
  useEffect(() => {
    if (targetId === null) return;
    if (isMe) {
      reloadCurrentUser();
      gaEventUserViewOwnProfile();
    } else {
      gaEventUserViewProfile("public app");
    }
  }, [targetId, isMe]);

  // Profile picture modal on first visit
  useEffect(() => {
    if (
      userQuery.state === "success" &&
      isMe &&
      !firstVisitFlagsStore.get().hasVisitedProfilePage &&
      userQuery.body.profilePicture?.name !== undefined &&
      userQuery.body.profilePicture.name !== null
    ) {
      setProfilePictureModalVisible(true);
      firstVisitFlagsStore.set((prev) => ({
        ...prev,
        hasVisitedProfilePage: true,
      }));
    }
  }, [isMe, userQuery]);

  // --- Api: created playlists ---
  const playlistsQuery = useApiQuery(
    "/webapp/private/playlists/user/:userId",
    targetId === null ? false : { param: { userId: targetId } }
  );
  const playlists =
    playlistsQuery.state === "success" ? playlistsQuery.body : [];

  const userBody = userQuery.state === "success" ? userQuery.body : undefined;

  // --- Api: bookmarked (saved) playlists ---
  const savedPlaylistsQuery = useApiQuery(
    "/webapp/private/users/:id/saved-playlists",
    targetId !== null && activeMainTab === MainTab.LIBRARY
      ? { param: { id: targetId } }
      : false
  );
  const savedPlaylists =
    savedPlaylistsQuery.state === "success" ? savedPlaylistsQuery.body : [];

  // --- Api: shared-with-me playlists (created in dashboard, shared with this user) ---
  const sharedWithMeQuery = useApiQuery(
    "/webapp/private/playlists/shared-with-me",
    isMe ? { query: { limit: 20, offset: 0 } } : false
  );
  const sharedWithMePlaylists =
    sharedWithMeQuery.state === "success" ? sharedWithMeQuery.body.docs : [];

  // --- Api: most recent AI conversation, on the engine that answers new questions ---
  const engine = useConversationEngine();
  const isLibraryOfMe = isMe && activeMainTab === MainTab.LIBRARY;
  const conversationsQuery = useApiQuery(
    "/webapp/private/conversations",
    isLibraryOfMe && engine === "legacy"
      ? { query: { limit: 1, offset: 0, sort: "newest" } }
      : false
  );
  const assistantQuery = useApiQuery(
    "/assistant/conversations",
    isLibraryOfMe && engine === "assistant"
      ? { query: { limit: 1, offset: 0, sort: "newest" } }
      : false
  );
  const legacyDoc =
    conversationsQuery.state === "success"
      ? conversationsQuery.body.docs.at(0)
      : undefined;
  const assistantDoc =
    assistantQuery.state === "success"
      ? assistantQuery.body.docs.at(0)
      : undefined;
  const latestConversation =
    legacyDoc !== undefined
      ? { engine: "legacy" as const, doc: legacyDoc }
      : assistantDoc !== undefined
        ? { engine: "assistant" as const, doc: assistantDoc }
        : null;

  // --- Api: most recent chat-with-paper session ---
  const cwyopQuery = useApiQuery(
    "/webapp/private/chat-with-paper/sessions",
    isMe && activeMainTab === MainTab.LIBRARY
      ? { query: { limit: 1, offset: 0 } }
      : false
  );
  const latestCwyop =
    cwyopQuery.state === "success" ? (cwyopQuery.body.docs[0] ?? null) : null;

  const latestAiChat = pickLatestAiChat(latestConversation, latestCwyop);

  // --- Api: last read article (lazy, library tab only) ---
  const lastReadArticleQuery = useApiQuery(
    "/webapp/private/users/:id/last-read-article",
    userBody !== undefined && activeMainTab === MainTab.LIBRARY
      ? { param: { id: userBody.id } }
      : false
  );

  function updateMainTab(tab: MainTab) {
    const next = new URLSearchParams(location.search);
    next.set("mainTab", tab);
    history.replace({
      pathname: location.pathname,
      search: next.toString(),
    });
  }

  if (userQuery.state === "idle" || userQuery.state === "pending") {
    return (
      <ScrollableLayout
        header={<ProfileHeader user={isMe ? currentUser : null} />}
      >
        <Loader expanded />
      </ScrollableLayout>
    );
  }

  if (userQuery.state === "error") {
    displayToast(t("Something went wrong. Please try again."), "error");
    history.goBack();
    return null;
  }

  // After early returns, userQuery.state === "success"
  const user = userQuery.body;

  // "Your interests" is a self-only concept ("personalize *your* feed"),
  // sourced from the user's selected JuisciTopics. Empty while the secondary
  // query loads/disabled — the section just shows its empty state, never blocks.
  const interests = myTopicsQuery.state === "success" ? myTopicsQuery.body : [];

  async function handleShareGameProfile(currentUser: CurrentUser) {
    try {
      const text = t(
        "Join me on Juisci and benefit from unlimited content by clicking on this link."
      );
      const url = `${globalThis.location.origin}/profile/user/${user.id}?shared=${currentUser.id}`;
      const outcome = await shareUrl(
        document.title,
        text,
        Promise.resolve(url)
      );
      gaEventUserShareProfile();

      if (outcome === "sent") displayToast(t("Profile shared"), "success");
    } catch (error) {
      console.error("Couldn't share game profile.", error);
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <ScrollableLayout
      header={<ProfileHeader user={isMe ? currentUser : null} />}
    >
      <ShareSheet type="profile" />

      <div className="flex flex-col gap-6 pt-5 pb-[calc(36px+var(--safe-area-bottom))]">
        {/* ── Identity ─────────────────────────────────────────── */}
        <div className="flex flex-col gap-6 px-5">
          <div className="flex items-start gap-4">
            <ProfilePicture
              user={user}
              height={52}
              className="shrink-0"
              badge={null}
            />

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <p className="m-0 text-[14px]/[1.25] font-semibold text-primary-black">
                {user.firstname !== null ? user.firstname : t("First Name")}{" "}
                {user.lastname !== null ? user.lastname : t("Last Name")}
              </p>
              {user.professionModel !== null ? (
                <p className="m-0 truncate text-[12px]/[1.4] font-normal text-ds-slate-600">
                  {getItemTranslation(user.professionModel)}
                </p>
              ) : null}
              {user.mainSpecialtyModel !== null ? (
                <p className="m-0 truncate text-[12px]/[1.4] font-normal text-ds-slate-600">
                  {getItemTranslation(user.mainSpecialtyModel)}
                </p>
              ) : null}
              {user.meta.creationDate !== null ? (
                <p className="m-0 mt-1 text-[12px]/[1.4] font-normal text-ds-slate-500">
                  {t("Joined on {{date}}", {
                    date: renderDate(user.meta.creationDate),
                  })}
                </p>
              ) : null}
            </div>
          </div>

          {user.link !== null ? (
            <a
              className="m-0 flex items-center gap-2 text-[12px] font-semibold text-primary-black underline"
              target="_blank"
              href={user.link}
              rel="noreferrer"
            >
              <LinkIcon />
              {user.link}
            </a>
          ) : null}

          {user.bio !== null ? (
            <ExpandableText
              isExpanded={isBioTextExpanded}
              setExpand={setBioTextExpanded}
              lines={10}
            >
              <p className="m-0 text-[14px] font-normal leading-normal text-primary-black">
                {user.bio}
              </p>
            </ExpandableText>
          ) : isMe ? (
            <Link
              to="/settings/edit/profile"
              className="flex w-full rounded-2xl border border-dashed border-ds-slate-200 px-5 py-8 no-underline"
            >
              <span className="text-[13px]/5 font-medium  tracking-tight text-ds-slate-500">
                {t("Add a biography to your profile")}
              </span>
            </Link>
          ) : (
            <p className="m-0 text-[14px] font-normal leading-normal text-primary-black">
              {t("No biography for this user")}
            </p>
          )}
        </div>

        <hr className="m-0 border-0 border-t border-ds-slate-200" />

        {/* ── Tabs ─────────────────────────────────────────────── */}
        <div className="flex items-center justify-center gap-3 px-5">
          <TabButton
            className="flex-1"
            icon={<IconStethoscope className="size-4" stroke={1.25} />}
            active={activeMainTab === MainTab.INFO}
            onClick={() => {
              updateMainTab(MainTab.INFO);
            }}
          >
            {t("Information")}
          </TabButton>
          <TabButton
            className="flex-1"
            icon={<IconFolderOpen className="size-4" stroke={1.25} />}
            active={activeMainTab === MainTab.LIBRARY}
            onClick={() => {
              updateMainTab(MainTab.LIBRARY);
            }}
          >
            {t("Library")}
          </TabButton>
        </div>

        {/* ── Information tab ──────────────────────────────────── */}
        {activeMainTab === MainTab.INFO ? (
          <div className="flex flex-col gap-8">
            {isMe && currentUser !== null ? (
              <div className="px-5">
                <ShareActivity
                  onClick={() => handleShareGameProfile(currentUser)}
                />
              </div>
            ) : null}

            <hr className="m-0 border-0 border-t border-ds-slate-200" />

            <section className="flex flex-col gap-4">
              <div className="px-5">
                <SectionHeader
                  title={isMe ? t("Your journals") : t("Followed journals")}
                  subtitle={null}
                  action={
                    user.journals.length > 0
                      ? {
                          kind: "link",
                          label: t("See all"),
                          to: {
                            pathname: isMe
                              ? "/profile/journals"
                              : `/profile/user/${user.id}/journals`,
                            state: { journals: user.journals },
                          },
                        }
                      : null
                  }
                />
              </div>

              {user.journalModels.length > 0 ? (
                <HScrollContainer>
                  {isMe ? (
                    <Link
                      to="/search?category=journals"
                      className="flex shrink-0 items-center gap-2 no-underline"
                    >
                      <Avatar
                        variant="icon"
                        size="md"
                        icon={<IconPlus className="size-5" stroke={1.5} />}
                        className={addJournalsAvatarClass}
                        badge={null}
                      />
                      <span className="text-[15px] font-semibold tracking-tight text-ds-black">
                        {t("Add journals")}
                      </span>
                    </Link>
                  ) : null}
                  {user.journalModels.map((journal) => (
                    <Link
                      key={journal.id}
                      to={`/journal/${journal.uid}`}
                      className="flex shrink-0 items-center gap-2 no-underline"
                    >
                      <Avatar
                        variant="icon"
                        size="md"
                        icon={<IconNews className="size-5" stroke={1.5} />}
                        badge={null}
                      />
                      <span className="whitespace-nowrap text-[15px] font-semibold tracking-tight text-ds-black">
                        {journal.name}
                      </span>
                    </Link>
                  ))}
                </HScrollContainer>
              ) : isMe ? (
                <div className="px-5">
                  <MissingProfileInfo
                    emoji="🔍"
                    text={t("Add your favorite journals")}
                    action={{ kind: "link", to: "/search?category=journals" }}
                  />
                </div>
              ) : (
                <p className="m-0 px-5 text-[13px] text-ds-slate-500">
                  {t("No saved journals for this user")}
                </p>
              )}
            </section>

            <hr className="m-0 border-0 border-t border-ds-slate-200" />

            {isMe ? (
              <section className="flex flex-col gap-4 px-5">
                <SectionHeader
                  title={t("Your interests")}
                  subtitle={t(
                    "Here are topics that you follow to personalize your feed."
                  )}
                  action={{
                    kind: "button",
                    label: t("Edit"),
                    onClick: () => {
                      setInterestsSheetOpen(true);
                    },
                  }}
                />
                {interests.length > 0 ? (
                  <div className="flex flex-wrap gap-3">
                    {interests.map((topic) => (
                      <span key={topic.uid} className={interestChipClass}>
                        {getItemTranslation(topic)}
                      </span>
                    ))}
                  </div>
                ) : (
                  <MissingProfileInfo
                    emoji="🧬"
                    text={t("Add topics to personalize your feed")}
                    action={{
                      kind: "button",
                      onClick: () => {
                        setInterestsSheetOpen(true);
                      },
                    }}
                  />
                )}
                <InterestsSheet
                  isOpen={isInterestsSheetOpen}
                  onClose={() => {
                    setInterestsSheetOpen(false);
                  }}
                />
              </section>
            ) : null}
          </div>
        ) : null}

        {/* ── Library tab ──────────────────────────────────────── */}
        {activeMainTab === MainTab.LIBRARY ? (
          <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-4 px-5">
              <SectionHeader
                title={t("Last read")}
                subtitle={null}
                action={null}
              />
              {lastReadArticleQuery.state === "idle" ||
              lastReadArticleQuery.state === "pending" ? (
                <SkeletonPublicationCard showImage showDescription />
              ) : lastReadArticleQuery.state === "success" &&
                lastReadArticleQuery.body !== null ? (
                <ArticlePublicationCard
                  roomId={null}
                  article={lastReadArticleQuery.body}
                  analyticsListName={AnalyticsItemList.PROFILE}
                  recommendation={null}
                  onClick={null}
                  className={null}
                  showImage={true}
                />
              ) : (
                <p className="m-0 text-[13px] text-ds-slate-500">
                  {t("No last read article")}
                </p>
              )}
            </section>

            <hr className="m-0 border-0 border-t border-ds-slate-200" />

            <section className="flex flex-col gap-4">
              <div className="px-5">
                <SectionHeader
                  title={
                    isMe ? (
                      t("Collections I created")
                    ) : (
                      <Trans
                        i18nKey="{{name}}'s playlist"
                        values={{ name: user.firstname ?? "" }}
                      />
                    )
                  }
                  subtitle={null}
                  action={
                    playlists.length > 0
                      ? {
                          kind: "link",
                          label: t("See all"),
                          to: {
                            pathname: isMe
                              ? "/profile/playlist"
                              : `/profile/user/${user.id}/playlist`,
                            state: { journals: user.journals },
                          },
                        }
                      : null
                  }
                />
              </div>

              {playlists.length > 0 ? (
                <AppCarousel slideGutter="page">
                  {playlists.map((playlist) => (
                    <PlaylistCard
                      key={playlist.id}
                      variant="dark"
                      playlist={playlist}
                      onClick={() => {
                        gaEventSelectItemPlaylist({
                          playlist,
                          listName: AnalyticsItemList.PROFILE,
                        });
                        history.push(`/profile/playlist/${playlist.id}`);
                      }}
                    />
                  ))}
                </AppCarousel>
              ) : isMe ? (
                <div className="px-5">
                  <MissingProfileInfo
                    emoji="➕"
                    text={t("Create your first playlist")}
                    action={{ kind: "link", to: "/profile/playlist/create" }}
                  />
                </div>
              ) : (
                <p className="m-0 px-5 text-[13px] text-ds-slate-500">
                  {t("No playlist for this user")}
                </p>
              )}
            </section>

            {savedPlaylists.length > 0 ? (
              <>
                <hr className="m-0 border-0 border-t border-ds-slate-200" />
                <section className="flex flex-col gap-4">
                  <div className="px-5">
                    <SectionHeader
                      title={
                        isMe ? (
                          t("Collections I saved")
                        ) : (
                          <Trans
                            i18nKey="{{name}}'s bookmarked playlists"
                            values={{ name: user.firstname ?? "" }}
                          />
                        )
                      }
                      subtitle={null}
                      action={{
                        kind: "link",
                        label: t("See all"),
                        to: {
                          pathname: isMe
                            ? "/profile/bookmarked-playlists"
                            : `/profile/user/${user.id}/bookmarked-playlists`,
                        },
                      }}
                    />
                  </div>
                  <AppCarousel slideGutter="page">
                    {savedPlaylists.map((playlist) => (
                      <PlaylistCard
                        key={playlist.id}
                        variant="dark"
                        playlist={playlist}
                        onClick={() => {
                          gaEventSelectItemPlaylist({
                            playlist,
                            listName: AnalyticsItemList.PROFILE,
                          });
                          history.push({
                            pathname: publicPlaylistLink(playlist),
                            state: { playlist },
                          });
                        }}
                      />
                    ))}
                  </AppCarousel>
                </section>
              </>
            ) : null}

            {isMe && sharedWithMePlaylists.length > 0 ? (
              <>
                <hr className="m-0 border-0 border-t border-ds-slate-200" />
                <section className="flex flex-col gap-4">
                  <div className="px-5">
                    <SectionHeader
                      title={t("My shared playlists")}
                      subtitle={null}
                      action={{
                        kind: "link",
                        label: t("See all"),
                        to: { pathname: "/profile/shared-playlist" },
                      }}
                    />
                  </div>
                  <AppCarousel slideGutter="page">
                    {sharedWithMePlaylists.slice(0, 5).map((playlist) => (
                      <PlaylistCard
                        key={playlist.id}
                        playlist={playlist}
                        variant="dark"
                        onClick={() => {
                          gaEventSelectItemPlaylist({
                            playlist,
                            listName: AnalyticsItemList.PROFILE,
                          });
                          history.push({
                            pathname: publicPlaylistLink(playlist),
                            state: { playlist },
                          });
                        }}
                      />
                    ))}
                  </AppCarousel>
                </section>
              </>
            ) : null}

            {isMe ? (
              <>
                <hr className="m-0 border-0 border-t border-ds-slate-200" />
                <section className="flex flex-col gap-4 px-5">
                  <SectionHeader
                    title={t("Last chat with AI")}
                    subtitle={null}
                    action={
                      latestAiChat !== null
                        ? {
                            kind: "node",
                            node: (
                              <Link
                                // No standalone history page: this lands on the
                                // JuisciGPT surface with the history drawer
                                // already revealed.
                                to={{
                                  pathname: gptNewChatPageLink(),
                                  state: { historyArrival: "revealing" },
                                }}
                                className="font-dm-sans shrink-0 text-xs font-bold tracking-wide text-ds-primary underline"
                              >
                                {t("See all")}
                              </Link>
                            ),
                          }
                        : null
                    }
                  />
                  {latestAiChat === null ? (
                    <Link
                      to={gptNewChatPageLink()}
                      className="block no-underline"
                    >
                      <Button
                        variant="primary"
                        size="md"
                        className="w-full"
                        leftIcon={<ChatWithPaperIcon />}
                        type="button"
                      >
                        {t("Start a new chat")}
                      </Button>
                    </Link>
                  ) : (
                    <LatestAiChatCard chat={latestAiChat} />
                  )}
                </section>
              </>
            ) : null}
          </div>
        ) : null}
      </div>

      <AddProfilePictureModal
        isVisible={isProfilePictureModalVisible}
        onClose={() => {
          setProfilePictureModalVisible(false);
        }}
      />
    </ScrollableLayout>
  );
}

function SectionHeader({
  title,
  subtitle,
  action,
}: {
  title: ReactNode;
  subtitle: string | null;
  action:
    | { kind: "link"; label: string; to: LinkProps["to"] }
    | { kind: "button"; label: string; onClick: () => void }
    | { kind: "node"; node: ReactNode }
    | null;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between gap-3">
        <SectionTitle title={title} />
        {action === null ? null : action.kind === "link" ? (
          <SectionLink to={action.to}>{action.label}</SectionLink>
        ) : action.kind === "button" ? (
          <button
            type="button"
            onClick={action.onClick}
            className="font-dm-sans shrink-0 cursor-pointer text-xs font-bold tracking-wide text-ds-primary underline"
          >
            {action.label}
          </button>
        ) : action.kind === "node" ? (
          action.node
        ) : (
          (() => {
            throw new Error(action satisfies never);
          })()
        )}
      </div>
      {subtitle !== null ? (
        <p className="m-0 text-[15px]/5 font-normal  tracking-tight text-ds-slate-600">
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function HScrollContainer({ children }: { children: ReactNode }) {
  return (
    <div className="scrollbar-hidden flex items-center gap-6 overflow-x-auto px-5">
      {children}
    </div>
  );
}

function MissingProfileInfo({
  emoji,
  text,
  action,
}: {
  emoji: string;
  text: string;
  action:
    { kind: "link"; to: string } | { kind: "button"; onClick: () => void };
}) {
  const className =
    "flex h-25 w-full items-center justify-center gap-5 rounded-2xl border-2 border-dashed border-ds-slate-300 no-underline";
  const content = (
    <>
      <span className="text-3xl opacity-30">{emoji}</span>
      <p className="m-0 text-[13px] font-normal text-ds-slate-400">{text}</p>
    </>
  );
  if (action.kind === "link") {
    return (
      <Link to={action.to} className={className}>
        {content}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={action.onClick}
      className={`${className} cursor-pointer bg-transparent`}
    >
      {content}
    </button>
  );
}
